import { ReactNode } from "react";
import { Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProStatus, ProFeature } from "@/hooks/useProStatus";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { isNativeCapacitorApp } from "@/lib/runtime";

interface ProGateProps {
  feature: ProFeature;
  children: ReactNode;
  fallback?: "blur" | "lock" | "hidden";
  className?: string;
}

/**
 * Wraps a component and shows an upgrade prompt if the user isn't Pro.
 * - "blur": Shows a blurred preview with an overlay CTA
 * - "lock": Shows a compact lock badge
 * - "hidden": Hides the content entirely
 */
export const ProGate = ({ feature, children, fallback = "blur", className }: ProGateProps) => {
  const { isPro, isProLoading, getFeatureLabel } = useProStatus();
  const navigate = useNavigate();
  // On the native iOS app we MUST route to the StoreKit-powered paywall,
  // not the Stripe-based web /pricing page (Apple Guideline 3.1.1, plus
  // window.open is a no-op inside WKWebView so the buttons would just spin).
  const pricingPath = isNativeCapacitorApp() ? "/ios/pricing" : "/pricing";

  if (isProLoading) return null;
  if (isPro) return <>{children}</>;

  if (fallback === "hidden") return null;

  if (fallback === "lock") {
    return (
      <button
        onClick={() => navigate(pricingPath)}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/5 border border-primary/10 text-sm text-muted-foreground hover:bg-primary/10 transition-colors w-full",
          className
        )}
      >
        <Lock className="h-3.5 w-3.5 text-primary flex-shrink-0" />
        <span>{getFeatureLabel(feature)}</span>
        <span className="ml-auto text-xs font-semibold text-primary">PRO</span>
      </button>
    );
  }

  // Blur fallback
  return (
    <div className={cn("relative", className)}>
      <div className="blur-[3px] pointer-events-none select-none opacity-60">
        {children}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-xl">
        <div className="text-center px-4 space-y-2">
          <div className="inline-flex p-2 bg-primary/10 rounded-full mb-1">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <p className="text-sm font-semibold text-foreground">{getFeatureLabel(feature)}</p>
          <p className="text-xs text-muted-foreground">Upgrade to Pro to unlock</p>
          <Button
            size="sm"
            className="mt-1 min-h-[36px]"
            onClick={() => navigate(pricingPath)}
          >
            Upgrade — $6.99/mo
          </Button>
        </div>
      </div>
    </div>
  );
};
